import {
  Stack,
  Alert,
  Divider,
  Typography,
  Button,
  CircularProgress,
  FormControlLabel,
  Switch,
} from "@mui/material";
import {
  useWebApiV1GetUserFeatureSwitchesQuery,
  useWebApiV1ListSiteGroupsQuery,
  useWebApiV1OrderQuery,
} from "@providers/hop-ord-server/api";
import { useParams, useLocation, Route, Routes } from "react-router-dom";
import { useState, useEffect } from "react";
import { toLocaleDateTimeString } from "@utils";
import OrderTrackerDrawer from "@pages/OrderTracker/components/OrderTrackerDrawer/OrderTrackerDrawer";
import PortaledMainBanner from "@pages/Layout/PortaledMainBanner";
import WarningIcon from "@mui/icons-material/Warning";
import WorkflowPageWrapper from "@pages/Layout/WorkflowPageWrapper";
import { PlanningTemplateApi, AutomatedPlanStatusPage } from "./pages";
import PlanReviewPage from "./pages/PlanReviewPage/PlanReviewPage";
import HandoverPage from "@pages/OrderTracker/HandoverPage";
import DrawerControlButtons from "@pages/OrderTracker/components/DrawerControlButtons";
import { Feature } from "@enums";
import useOrderTrackerDrawer from "@pages/OrderTracker/components/OrderTrackerDrawer/useOrderTrackerDrawer";
import RefreshIcon from "@mui/icons-material/Refresh";

const PAGE_WIDTH = "900px"; // Need to enforce the width so the PDF generation is consistent

const DosimetristPlanningAutomation = () => {
  const { orderId } = useParams();
  const {
    drawerOpen,
    setDrawerOpen,
    selectedDrawerPanel,
    setSelectedDrawerPanel,
  } = useOrderTrackerDrawer();
  const location = useLocation();
  const [refreshingStructures, setRefreshingStructures] = useState(false);
  const [isComparisonMode, setIsComparisonMode] = useState(false);

  // Listen for refresh status updates
  useEffect(() => {
    const handleRefreshStart = () => setRefreshingStructures(true);
    const handleRefreshEnd = () => setRefreshingStructures(false);

    window.addEventListener("refreshStructuresStart", handleRefreshStart);
    window.addEventListener("refreshStructuresEnd", handleRefreshEnd);

    return () => {
      window.removeEventListener("refreshStructuresStart", handleRefreshStart);
      window.removeEventListener("refreshStructuresEnd", handleRefreshEnd);
    };
  }, []);

  const { data: orderData, isLoading: orderLoading } = useWebApiV1OrderQuery({
    orderId: Number(orderId),
  });

  const { data: featureSwitchData, isLoading: featureSwitchesLoading } =
    useWebApiV1GetUserFeatureSwitchesQuery();

  const { data: siteGroupData, isLoading: siteGroupLoading } =
    useWebApiV1ListSiteGroupsQuery({
      orderId: Number(orderId),
    });

  const editingDatetime = orderData?.editingDatetime
    ? toLocaleDateTimeString(orderData?.editingDatetime || "")
    : "";

  const showPlanReview = featureSwitchData?.find(
    (feature) => feature.name === Feature.PLAN_REVIEW,
  );

  if (featureSwitchesLoading || orderLoading || siteGroupLoading) {
    return <></>;
  }

  // TODO: remove this jank when plan_review_page feature switch is removed
  const planReviewPageTitles = location.pathname.endsWith("/handover")
    ? "Step 4: Handover"
    : location.pathname.endsWith("/plan-review")
      ? "Plan Review"
      : "Plan Generation";

  const nonPlanReviewPageTitles = location.pathname.endsWith("/handover")
    ? "Step 3: Handover"
    : "Plan Generation";

  const planRevewIndex = location.pathname.endsWith("/handover")
    ? 2 // Show Plan Review step as active when on handover page
    : location.pathname.endsWith("/plan-review")
      ? 2
      : 1;
  const nonPlanReviewIndex = location.pathname.endsWith("/handover")
    ? 1 // Show Plan Generation step as active when on handover page
    : 1;

  const pageTitle = showPlanReview
    ? planReviewPageTitles
    : nonPlanReviewPageTitles;
  const currentPageIndex = showPlanReview ? planRevewIndex : nonPlanReviewIndex;

  const handleCompareToggle = () => {
    setIsComparisonMode(!isComparisonMode);
  };

  // Check if we're on the plan-review page
  const isPlanReviewPage = location.pathname.endsWith("/plan-review");

  return (
    <WorkflowPageWrapper
      currentPageIndex={currentPageIndex}
      orderId={Number(orderId)}
    >
      {orderData && !orderData?.isLatest && (
        <PortaledMainBanner>
          <Stack>
            <Alert
              severity="warning"
              sx={{ justifyContent: "center" }}
              icon={<WarningIcon />}
            >
              <Typography>{`This order was last edited at ${editingDatetime} and has not yet been submitted. Click 'Edit' to continue.`}</Typography>
            </Alert>
            <Divider />
          </Stack>
        </PortaledMainBanner>
      )}
      <Stack
        maxWidth={isPlanReviewPage ? "1500px" : PAGE_WIDTH}
        width={isPlanReviewPage ? "auto" : 1}
        marginLeft="auto"
        marginRight="auto"
        padding={2}
        paddingTop={3}
        paddingBottom={isPlanReviewPage && isComparisonMode ? 0 : 2}
        paddingLeft={isPlanReviewPage ? 8 : 0}
        paddingRight={isPlanReviewPage ? 8 : 0}
        gap={2}
      >
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
        >
          <Typography variant="h5">{pageTitle}</Typography>
          <Stack direction="row" alignItems="center" gap={2}>
            {!location.pathname.endsWith("/plan-review") &&
              !location.pathname.endsWith("/handover") &&
              !location.pathname.endsWith("/automated") && (
                <Button
                  variant="outlined"
                  size="small"
                  disabled={refreshingStructures}
                  startIcon={
                    refreshingStructures ? (
                      <CircularProgress size={16} />
                    ) : (
                      <RefreshIcon />
                    )
                  }
                  onClick={() => {
                    const refreshEvent = new CustomEvent("refreshStructures");
                    window.dispatchEvent(refreshEvent);
                  }}
                >
                  Refresh CT & RTstruct
                </Button>
              )}
            {location.pathname.endsWith("/plan-review") && (
              <FormControlLabel
                control={
                  <Switch
                    checked={isComparisonMode}
                    onChange={handleCompareToggle}
                    color="primary"
                    sx={{
                      "& .MuiSwitch-switchBase.Mui-checked": {
                        color: "#745de5",
                      },
                      "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track":
                        {
                          backgroundColor: "#745de5",
                        },
                    }}
                  />
                }
                label="Compare"
                labelPlacement="start"
              />
            )}
          </Stack>
        </Stack>
        <DrawerControlButtons
          setDrawerOpen={setDrawerOpen}
          setSelectedDrawerPanel={setSelectedDrawerPanel}
          alwaysShow={isPlanReviewPage}
        />

        {orderData && (
          <Stack
            width={1}
            height={1}
            marginTop={2}
            marginBottom={isPlanReviewPage && isComparisonMode ? 0 : 2}
            gap={2}
          >
            <Routes>
              <Route
                path="/"
                index
                element={<PlanningTemplateApi orderId={Number(orderId)} />}
              />
              <Route
                path="/automated"
                element={<AutomatedPlanStatusPage orderId={Number(orderId)} />}
              />
              <Route
                path="/plan-review"
                element={<PlanReviewPage isComparisonMode={isComparisonMode} />}
              />
              <Route
                path="/handover"
                element={<HandoverPage order={orderData} />}
              />
            </Routes>
          </Stack>
        )}
      </Stack>
      <OrderTrackerDrawer
        orderId={Number(orderId)}
        open={drawerOpen}
        setOpen={setDrawerOpen}
        selectedPanel={selectedDrawerPanel}
        setSelectedPanel={setSelectedDrawerPanel}
        orderData={orderData}
        siteGroupData={siteGroupData || []}
        forceCollapsed={isPlanReviewPage}
      />
    </WorkflowPageWrapper>
  );
};

export default DosimetristPlanningAutomation;
